import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { MessageSquare, MapPin, BadgeCheck, Send, Flag, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { useAuth } from "../context/AuthContext";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Find a Team", "Pickup Games", "Coaching", "Volunteering", "Equipment Exchange", "Match Reports", "Local Needs"];

export default function CommunityPage() {
  const { user, login } = useAuth();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", category: CATEGORIES[0], place: "" });
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/forum/posts`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    try {
      await axios.post(`${API}/forum/posts`, form, { withCredentials: true });
      toast.success("Post published to the community board");
      setForm({ title: "", body: "", category: CATEGORIES[0], place: "" });
      load();
    } catch {
      toast.error("Could not publish — are you signed in?");
    }
  };

  const sendReply = async (postId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/forum/posts/${postId}/replies`, { body: replyText }, { withCredentials: true });
      setReplyText("");
      setReplyFor(null);
      load();
    } catch {
      toast.error("Could not reply — are you signed in?");
    }
  };

  const reportPost = async (postId) => {
    try {
      const res = await axios.post(`${API}/forum/posts/${postId}/report`, {}, { withCredentials: true });
      toast.success(res.data.already_reported ? "You already reported this post" : "Reported to moderators — thank you for keeping the community safe");
      load();
    } catch {
      toast.error("Sign in to report a post");
    }
  };

  return (
    <div>
      <PageHero topic="community sports team huddle" title="Community Forums & Participation" subtitle="Find a team, organize pickup games, volunteer, trade equipment and share match reports — moderated to keep local sport welcoming." listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <AIContent topic="Building healthy local sports communities" context="Editorial on community participation: finding teams, volunteering, respectful rivalry, moderation and safeguarding in local sport forums." />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="new-post-panel">
              <h3 className="text-lg font-bold text-slate-900"><T>Start a discussion</T></h3>
              {user ? (
                <form onSubmit={submit} className="mt-4 space-y-3">
                  <input data-testid="post-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#0055FF]" />
                  <textarea data-testid="post-body-input" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Share details with your community…" rows={4} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#0055FF]" />
                  <div className="flex gap-2">
                    <select data-testid="post-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-1/2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0055FF]">
                      {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <input data-testid="post-place-input" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder="City, Country" className="w-1/2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#0055FF]" />
                  </div>
                  <button data-testid="post-submit-button" type="submit" className="w-full rounded-xl bg-[#0055FF] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0040CC]"><T>Publish post</T></button>
                  <p className="text-[11px] text-slate-400"><T>Posts appear as community reports until verified by organizers. Be kind — no harassment, doxxing or medical misinformation.</T></p>
                </form>
              ) : (
                <div className="mt-4 rounded-xl bg-blue-50 p-5 text-center">
                  <p className="text-lg font-extrabold text-[#0A2540]">SportsPulse<span className="text-[#0055FF]">.pro</span></p>
                  <p className="mt-1 text-sm text-slate-600"><T>Sign in to post and reply.</T></p>
                  <button data-testid="community-login-button" onClick={login} className="mt-3 rounded-full bg-[#0055FF] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0040CC]"><T>Join SportsPulse</T></button>
                  <p className="mt-3 text-[11px] font-semibold text-slate-400" data-testid="powered-by-socialpulse"><T>Powered by SocialPulse</T></p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4 lg:col-span-2" data-testid="forum-posts">
            {posts.map((p) => (
              <div key={p.post_id} data-testid={`forum-post-${p.post_id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase text-[#0055FF]">{p.category}</span>
                  {p.place && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold text-slate-500"><MapPin className="h-3 w-3" />{p.place}</span>}
                  {p.label === "verified-org" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold text-emerald-700"><BadgeCheck className="h-3 w-3" /><T>Verified org</T></span>
                  ) : (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-700"><T>Community</T></span>
                  )}
                  {(p.report_count || 0) >= 2 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-[10px] font-bold text-red-700" data-testid={`under-review-${p.post_id}`}><ShieldAlert className="h-3 w-3" /><T>Under review</T></span>
                  )}
                </div>
                <h3 className="mt-3 text-base font-bold text-slate-900">{p.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.body}</p>
                <p className="mt-3 text-[11px] text-slate-400">{p.author_name} · {new Date(p.created_at).toLocaleDateString()}</p>
                {p.replies?.length > 0 && (
                  <div className="mt-4 space-y-2 border-t border-slate-100 pt-3">
                    {p.replies.map((r) => (
                      <div key={r.reply_id} className="rounded-xl bg-slate-50 px-4 py-2.5">
                        <p className="text-sm text-slate-700">{r.body}</p>
                        <p className="mt-1 text-[10px] text-slate-400">{r.author_name}</p>
                      </div>
                    ))}
                  </div>
                )}
                {user && (
                  replyFor === p.post_id ? (
                    <div className="mt-3 flex gap-2">
                      <input data-testid={`reply-input-${p.post_id}`} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply…" className="flex-1 rounded-xl border border-slate-200 px-4 py-2 text-sm outline-none focus:border-[#0055FF]" />
                      <button data-testid={`reply-send-${p.post_id}`} onClick={() => sendReply(p.post_id)} className="rounded-xl bg-[#0055FF] px-4 py-2 text-white hover:bg-[#0040CC]"><Send className="h-4 w-4" /></button>
                    </div>
                  ) : (
                    <div className="mt-3 flex items-center gap-4">
                      <button data-testid={`reply-open-${p.post_id}`} onClick={() => { setReplyFor(p.post_id); setReplyText(""); }} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0055FF] hover:underline">
                        <MessageSquare className="h-3.5 w-3.5" /> <T>Reply</T>
                      </button>
                      <button data-testid={`report-button-${p.post_id}`} onClick={() => reportPost(p.post_id)} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-red-500">
                        <Flag className="h-3.5 w-3.5" /> <T>Report</T>
                      </button>
                    </div>
                  )
                )}
              </div>
            ))}
            {posts.length === 0 && <p className="py-10 text-center text-sm text-slate-500"><T>No discussions yet — start the first one!</T></p>}
          </div>
        </div>
      </div>
    </div>
  );
}
